/* eslint-disable no-underscore-dangle */
import { createRoot, hydrateRoot } from 'react-dom/client';
import { moveStyles } from 'used-styles/moveStyles';
import { createBrowserRouter } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import {
  FacebookPixelHandler,
  GoogleAnalyticsHandler,
  LoggingAnalyticsHandler,
} from './analytics/handlers';
import defaultConfig from './config/configDefault';
import appSettings from './config/settings';
import { authInfo } from './ducks/auth.duck';
import { fetchAppAssets } from './ducks/hostedAssets.duck';
import { fetchCurrentUser } from './ducks/user.duck';
import configureStore from './store';
import './styles/marketplaceDefaults.css';
import './v2/tailwind.css';
import * as apiUtils from './util/api';
import { isReloadInFlight, reloadIfStale } from './util/appVersion';
import {
  clearChunkReloadAttempts,
  isChunkLoadError,
  isChunkReloadInFlight,
  noteNavigationTarget,
  recoverFromChunkError,
} from './util/chunkReload';
import { armSplashFailsafe, hideSplash, showSplash } from './util/splash';
import { getCookie, getSuperLoginAsUser } from './util/cookie';
import * as log from './util/log';
import { createInstance, types as sdkTypes } from './util/sdkLoader';
import { saveCookieConsent } from './extensions/cookie-consent/components/CookieConsent/CookieConsent.duck';

import { ClientApp } from './app';
import routeConfiguration from './routing/routeConfiguration';
import { mergeConfig } from './util/configHelpers';
import { RootRoute } from './routing/AppRouter';

// Catch chunk load failures after a deploy and reload to get fresh HTML. The
// attempt guard lives in util/chunkReload.js — see the comment there for why it
// counts attempts rather than trusting a cooldown (CH-460).
window.addEventListener('error', (e) => {
  if (isChunkLoadError(e.message)) recoverFromChunkError();
});
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError(e.reason?.message)) recoverFromChunkError();
});
window.addEventListener('vite:preloadError', () => recoverFromChunkError());

// A deploy removes the previous build's chunks, so a long-lived tab can be left
// holding references that no longer resolve — the reload above is the recovery
// path when it tries to load one. To catch staleness *before* that happens,
// every /api response also carries an X-App-Version header (observed in
// util/appVersion.js, which reloads on the spot when stale). Note that header
// only moves when package.json's version does, so it cannot see two builds of
// the *same* version being served side by side.
//
// When a user comes back to the tab, gate them behind the splash while the
// version check runs: fresh tabs get a quick fade-out, stale tabs reload under
// the splash and come back on the new bundle without ever showing old content.
// pingVersion throttles itself to one check per 5 minutes, so quick tab
// switches don't flash the splash.
const gateOnVersionCheck = () => {
  const pending = apiUtils.pingVersion();
  if (!pending) return;
  showSplash();
  pending.then(() => {
    if (!isReloadInFlight()) hideSplash();
  });
};
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') gateOnVersionCheck();
});
// Back/forward-cache restores resurrect the old JS heap without a network
// load — and same-tab restores don't fire visibilitychange, so gate here too.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) gateOnVersionCheck();
});

const moveStylesFn = () => {
  const allRootStyleSheets = document.querySelectorAll('link[rel="stylesheet"]');
  const rootStyleSheet = Array.from(allRootStyleSheets).find((link) => {
    return link.getAttribute('href').match(/\/assets\/index-.*\.css/);
  });

  if (rootStyleSheet) {
    const linkTags = document.querySelectorAll('[data-used-styles]');
    for (let i = 0; i < linkTags.length; i++) {
      document.head.insertBefore(linkTags[i].cloneNode(true), rootStyleSheet.nextSibling);
    }
    for (let i = 0; i < linkTags.length; i++) {
      const parent = linkTags[i].parentNode;
      if (parent) {
        parent.removeChild(linkTags[i]);
      }
    }
  } else {
    moveStyles();
  }
};

const render = async (store, shouldHydrate) => {
  // The splash from index.html is covering the page; run the version check
  // under it while the app boots. Never let it hang past the fail-safe.
  armSplashFailsafe();
  const versionPing = apiUtils.pingVersion();
  try {
    const state = store.getState();
    const {
      hostedAssets: { version: cdnAssetsVersion },
      auth: { authInfoLoaded },
    } = state;
    await (authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo()));
    store.dispatch(fetchCurrentUser());
    const fetchedAppAssets = await store.dispatch(
      fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)
    );
    const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
    const translations = translationsRaw?.data || {};
    const configEntries = Object.entries(rest);
    const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
      return { ...collectedData, [name]: content.data || {} };
    }, {});

    const appConfig = mergeConfig(hostedConfig, defaultConfig);

    const cookieConsent = getCookie('__cookie_consent');
    store.dispatch(saveCookieConsent(cookieConsent));

    const routeConfig = await routeConfiguration({
      config: appConfig,
      store,
      location: window.location,
    });

    const appProps = {
      store,
      hostedTranslations: translations,
      routeConfig,
      appConfig,
    };

    const router = createBrowserRouter(
      [{ path: '/', Component: RootRoute, children: routeConfig }],
      {
        future: {
          v7_partialHydration: true,
        },
      }
    );

    // Fallback for staleness observed on a mutation response (which never
    // reloads on the spot — see util/appVersion.js): swap bundles at the next
    // route navigation instead.
    //
    // The same subscription tracks where a navigation is headed, so that a
    // chunk failure mid-navigation reloads the destination rather than the page
    // the user is still technically on (util/chunkReload.js).
    router.subscribe((state) => {
      if (state.navigation.state === 'loading' && state.navigation.location) {
        const { pathname, search, hash } = state.navigation.location;
        const href = pathname + search + hash;
        noteNavigationTarget(href);
        reloadIfStale(href);
      } else if (state.navigation.state === 'idle') {
        noteNavigationTarget(null);
      }
    });

    // Route loaders are marked hydrate=true (routes.utils.jsx), so on mount
    // the router discards the SSR DOM and shows the white RouteFallback while
    // the route chunk loads and loadData re-runs. Resolves when that initial
    // load settles, so the splash fades to real content instead of that flash.
    const routerReady = new Promise((resolve) => {
      if (router.state.initialized) {
        resolve();
        return;
      }
      const unsubscribe = router.subscribe((state) => {
        if (state.initialized) {
          unsubscribe();
          resolve();
        }
      });
    });

    // Parse PostHog bootstrap data if available (injected by SSR)
    const posthogBootstrap = window.__POSTHOG_BOOTSTRAP__
      ? JSON.parse(window.__POSTHOG_BOOTSTRAP__)
      : null;

    // Initialize posthog with bootstrap data for immediate flag availability
    const posthogConfig = {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: '2025-11-30',
      ...(posthogBootstrap?.distinctID && {
        bootstrap: {
          distinctID: posthogBootstrap.distinctID,
          featureFlags: posthogBootstrap.featureFlags || {},
          featureFlagPayloads: posthogBootstrap.featureFlagPayloads || {},
        },
      }),
    };

    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, posthogConfig);
    // Super property: stamps every event (anonymous included) with the bundle
    // version this tab is actually running — unlike the person property set at
    // identify(), which only covers logged-in users and lags behind deploys.
    posthog.register({ app_version: __APP_VERSION__ });
    window.posthog = posthog;

    const rootElement = document.getElementById('root');
    if (shouldHydrate) {
      moveStylesFn();
      hydrateRoot(
        rootElement,
        <PostHogProvider client={posthog}>
          <ClientApp {...appProps} router={router} />
        </PostHogProvider>
      );
    } else {
      const root = createRoot(rootElement);
      root.render(
        <PostHogProvider client={posthog}>
          <ClientApp {...appProps} router={router} />
        </PostHogProvider>
      );
    }
    // Note there is deliberately no chunk-reload reset here: rendering the
    // shell succeeds before the route's own chunk is imported, so clearing at
    // this point let a failing route reload forever (CH-460). The reset waits
    // for routerReady below.

    // Hold the gate until the boot version check has answered AND the router
    // has finished its initial hydration load (see routerReady above). If the
    // check flagged a stale bundle, the reload is already in flight — keep the
    // splash up so old content never shows; the fresh page boots behind its
    // own splash.
    await Promise.all([versionPing, routerReady]);

    // The initial route settled without error, so its lazy chunk imported and
    // linked against the rest of the graph — the only evidence that actually
    // rules out the mismatch chunkReload.js guards against. A load that errored
    // leaves the counter alone, so the next failure still counts against the
    // budget instead of restarting it.
    if (!router.state.errors) clearChunkReloadAttempts();

    if (!isReloadInFlight() && !isChunkReloadInFlight()) hideSplash();
  } catch (e) {
    // Fail open: a broken boot must never leave users staring at the splash.
    hideSplash();
    log.error(e, 'browser-side-render-failed');
  }
};

const setupAnalyticsHandlers = (googleAnalyticsId) => {
  const handlers = [new FacebookPixelHandler({ devMode: appSettings.dev })];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  // Add Google Analytics 4 (GA4) handler if tracker ID is found
  if (googleAnalyticsId) {
    if (googleAnalyticsId.indexOf('G-') !== 0) {
      console.warn(
        'Google Analytics 4 (GA4) should have measurement id that starts with "G-" prefix'
      );
    } else {
      handlers.push(new GoogleAnalyticsHandler());
    }
  }

  return handlers;
};

log.setup();

const {
  baseUrl: sdkBaseUrl,
  assetCdnBaseUrl: sdkAssetCdnBaseUrl,
  loginAsBaseUrl,
} = appSettings.sdk;

const baseUrl = sdkBaseUrl ? { baseUrl: sdkBaseUrl } : {};
const assetCdnBaseUrl = sdkAssetCdnBaseUrl ? { assetCdnBaseUrl: sdkAssetCdnBaseUrl } : {};

const preloadedState = window.__PRELOADED_STATE__ || '{}';
const shouldHydrate = window.__PRELOADED_STATE__ !== undefined;
const initialState = JSON.parse(preloadedState, sdkTypes.reviver);

const loginAsUser = getSuperLoginAsUser();
const overrideBaseUrlMaybe = loginAsUser ? { baseUrl: loginAsBaseUrl } : {};

const sdk = createInstance({
  transitVerbose: appSettings.sdk.transitVerbose,
  clientId: appSettings.sdk.clientId,
  secure: appSettings.usingSSL,
  typeHandlers: apiUtils.typeHandlers,
  ...baseUrl,
  ...overrideBaseUrlMaybe,
  ...assetCdnBaseUrl,
});

// Note: on localhost:3000, you need to use environment variable.
const googleAnalyticsIdFromSSR = initialState?.hostedAssets?.googleAnalyticsId;
const googleAnalyticsId = googleAnalyticsIdFromSSR || import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
const analyticsHandlers = setupAnalyticsHandlers(googleAnalyticsId);
const store = configureStore(initialState, sdk, analyticsHandlers);

require('./util/polyfills');

render(store, shouldHydrate);

if (appSettings.dev) {
  window.app = {
    appSettings,
    defaultConfig,
    sdk,
    sdkTypes,
    store,
  };
}

// Show warning if CSP is not enabled
const CSP = import.meta.env.VITE_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && import.meta.env.VITE_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable VITE_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}
